import dateFormat, { masks } from 'dateformat';
import { getPrecisionOnCurrency } from './currencyPrecision';
import { formatNumberWithCommas, groupInteger } from './numberFormat';

export { getPrecisionOnCurrency };
export { formatNumberWithCommas, groupInteger };

export const ONE_DAY_PS_PRODUCT_ID = 4776;

export const formatDate = (date, format) => {
  return dateFormat(date, format);
};

export const showDate = (date) => {
  if (!date) {
    return '';
  }
  if (typeof date.getMonth !== 'function') {
    date = new Date(Date.parse(date));
  }
  return formatDate(date, 'dd mmm yyyy');
};

export const showDateTime = (date) => {
  if (!date) {
    return '';
  }
  if (typeof date.getMonth !== 'function') {
    date = new Date(Date.parse(date));
  }
  return formatDate(date, 'dd mmm yyyy HH:MM') + ' UTC';
};

export const showUTCDateTime = (dateStr) => {
  const date = new Date(dateStr);
  const datePart = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${datePart} ${timePart} UTC`;
};

export const formatUTCForPayload = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime()) || d.getUTCFullYear() < 2020) {
    throw new Error(`formatUTCForPayload: invalid date "${dateStr}"`);
  }
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear() +
    '-' +
    pad(d.getUTCMonth() + 1) +
    '-' +
    pad(d.getUTCDate()) +
    ' ' +
    pad(d.getUTCHours()) +
    ':' +
    pad(d.getUTCMinutes()) +
    ':' +
    pad(d.getUTCSeconds())
  );
};
export const payoutDateTime = (date) => {
  if (!date) {
    return '';
  }
  if (typeof date.getMonth !== 'function') {
    date = new Date(Date.parse(date));
  }
  return formatDate(date, 'ddd mmm dd HH:MM yyyy') + ' UTC';
};

export const showAddress = (address) => {
  return address;
};

export const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

export const showPercent = (value, precision = 4) => {
  return '' + (value * 100).toFixed(precision) + '%';
};

export const showStrikePrice = (amount, currency) => {
  return showPrice(amount, currency);
};

export const roundDownToDecimalPlaces = (number, decimalPlaces) => {
  const str = number.toFixed(decimalPlaces + 2);
  const dot = str.indexOf('.');
  if (dot === -1) return number;
  return parseFloat(str.slice(0, dot + 1 + decimalPlaces));
};

export const truncateToDecimalPlaces = (number, decimalPlaces) => {
  const str = number.toFixed(decimalPlaces + 2);
  const dot = str.indexOf('.');
  if (dot === -1) return number;
  return parseFloat(str.slice(0, dot + 1 + decimalPlaces));
};

export const roundDownAmount = (amount, currency, precision = 8) => {
  const finalPrecision = currency
    ? getPrecisionOnCurrency(currency)
    : precision;
  const str = amount.toFixed(finalPrecision + 2);
  const dot = str.indexOf('.');
  if (dot === -1) return parseFloat(amount).toFixed(finalPrecision);
  return parseFloat(str.slice(0, dot + 1 + finalPrecision)).toFixed(
    finalPrecision
  );
};

//truncate/round down given number
export const truncateAmount = (amount, currency, precision = 8) => {
  const finalPrecision = currency
    ? getPrecisionOnCurrency(currency)
    : precision;
  const str = amount.toFixed(finalPrecision + 2);
  const dot = str.indexOf('.');
  if (dot === -1) return parseFloat(amount).toFixed(finalPrecision);
  return parseFloat(str.slice(0, dot + 1 + finalPrecision)).toFixed(
    finalPrecision
  );
};

//Round given number
export const roundAmount = (amount, currency, precision = 8) => {
  const finalPrecision = currency
    ? getPrecisionOnCurrency(currency)
    : precision;
  const factor = Math.pow(10, finalPrecision);
  const roundedValue = Math.round(amount * factor) / factor;
  return roundedValue.toFixed(finalPrecision);
};

//Format toFixed number
export const showAmount = (
  amount,
  currency = null,
  precision = 8,
  showTrailingZeros = false
) => {
  const finalPrecision = currency
    ? getPrecisionOnCurrency(currency)
    : precision;
  const amountToFixed = Number(amount).toFixed(finalPrecision);
  return formatNumberWithCommas(amountToFixed, showTrailingZeros);
};

//Format rounded number
export const showRoundAmount = (amount, currency = null, precision = 8) => {
  const roundedAmount = roundAmount(amount, currency, precision);
  return formatNumberWithCommas(roundedAmount);
};

//Format rounded down number
export const showRoundDownAmount = (amount, currency = null, precision = 8) => {
  const roundedDownAmount = roundDownAmount(amount, currency, precision);
  return formatNumberWithCommas(roundedDownAmount);
};

export const showPrice = (
  amount,
  currency = null,
  precision = 8,
  showTrailingZeros = false
) => {
  const formattedAmount = showAmount(
    amount,
    currency,
    precision,
    showTrailingZeros
  );
  return currency
    ? `${formattedAmount} ${currency.toUpperCase()}`
    : formattedAmount;
};

export const showOption = (value, options) => {
  for (const op of options) {
    if (op.value == value) {
      return op.label;
    }
  }
  return null;
};

export const toDate = (dateStr) => {
  if (!dateStr || dateStr == '') {
    return null;
  }

  return new Date(Date.parse(dateStr));
};

export const fromDate = (date) => {
  if (!date) {
    return '';
  }
  return (
    '' +
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1).toString().padStart(2, '0') +
    '-' +
    date.getDate().toString().padStart(2, '0')
  );
};

export const ageFromDate = (dob) => {
  const month_diff = Date.now() - dob.getTime();
  const age_dt = new Date(month_diff);
  const year = age_dt.getUTCFullYear();
  return Math.abs(year - 1970);
};

export const toUsdc = (currency) => {
  if (!currency) {
    return currency;
  }
  if (
    currency.toLowerCase() == 'usd' ||
    currency.toLowerCase() == 'usdc' ||
    currency.toLowerCase() == 'usdt'
  ) {
    return currency.toUpperCase();
  }
  return currency.toUpperCase();
};

// PB-1046: 'USD' is a denomination label; the settleable asset behind it is USDC.
// Mirrors backend CurrencyDecimalUtils.normalizeForSettlement — USDT is a distinct
// asset and is deliberately NOT coerced. Use for display, not for wallet matching:
// invest_ccy must stay the client's real wallet currency or backend wallet
// validation (wallet.currency vs orderProduct.investAmountCcy) fails.
export const toSettlementCcy = (currency) => {
  if (!currency) {
    return currency;
  }
  const upper = currency.toUpperCase();
  return upper === 'USD' ? 'USDC' : upper;
};
